// Admin › Update Checks › Live monitor.
//
// One screen for the whole OTA conversation, per Scout, as it happens:
//   • server side — every request a Scout makes to the update servers
//     (manifest fetch, APK download), read from the ota-updater worker's
//     request log through /api/admin/ota/live/requests (polled every 3 s;
//     Cloudflare's own ingestion adds a few seconds);
//   • device side — every "check for updates" report a Scout posts
//     (/api/admin/ota/live/reports, polled every 3 s);
//   • what is on offer — the live stable + platform manifests (every 60 s).
//
// Every line carries a PLATFORM / STABLE badge and a plain-English sentence;
// "Technical view" adds device id, path, HTTP status, timing, IP, ray id and
// the raw report fields. Read-only.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, PauseIcon, PlayIcon, SignalIcon } from '@heroicons/react/24/outline';
import { api, type OtaLiveManifest, type OtaLiveReport, type OtaLiveRequest } from '../../services/api';

type Channel = 'platform' | 'stable';

interface ConfirmEvent {
  kind: 'confirm';
  id: string;
  ts: number;
  deviceId: string;
  app: string;
  version: string | null;
  code: number | null;
  reportId: string;
  downloadTs: number;
}
type LiveEvent = OtaLiveRequest | OtaLiveReport | ConfirmEvent;

interface DeviceInfo {
  id: string;
  vessel: string | null;
  /** Definitive: from the X-Update-Channel header on a manifest fetch. */
  channel: Channel | null;
  channelSeenAt: number;
  /** Hint: from the signing flag in a check report. */
  channelHint: Channel | null;
}

const APP_ORDER = ['launcher', 'vn', 'va', 'vt', 'wv', 'th', 'vc', 'gps'];
const REQUEST_POLL_MS = 3_000;
const REPORT_POLL_MS = 3_000;
const MANIFEST_POLL_MS = 60_000;
/** Re-query this much history on every poll so late-ingested events are caught. */
const REQUEST_LOOKBACK_MS = 3 * 60_000;
const INITIAL_REQUEST_HOURS = 6;
const INITIAL_REPORT_DAYS = 3;
const MAX_EVENTS = 6_000;

const pad = (n: number) => String(n).padStart(2, '0');
const hms = (ts: number) => { const d = new Date(ts); return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`; };
const dmy = (ts: number) => { const d = new Date(ts); return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`; };
const ago = (ts: number | null | undefined) => {
  if (!ts) return 'never';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
};
const fmtMB = (b?: number) => (b ? `${(b / 1e6).toFixed(1)} MB` : '');

function ChannelBadge({ channel, title, big }: { channel: Channel | 'unknown'; title?: string; big?: boolean }) {
  const cls = channel === 'platform' ? 'bg-violet-700 text-white' : channel === 'stable' ? 'bg-cyan-700 text-white' : 'bg-slate-600 text-white';
  return (
    <span
      title={title}
      className={`inline-block rounded-md font-extrabold tracking-wide ${big ? 'px-3 py-1 text-[15px]' : 'px-2 py-0.5 text-[13px]'} ${cls}`}
    >
      {channel.toUpperCase()}
    </span>
  );
}

const STATUS_CLS: Record<string, string> = {
  ok: 'bg-emerald-500/20 text-emerald-300',
  offer: 'bg-amber-500/20 text-amber-300',
  dl: 'bg-sky-500/25 text-sky-300',
  wait: 'bg-violet-500/25 text-violet-200',
  bad: 'bg-red-500/20 text-red-300',
  na: 'bg-slate-500/20 text-slate-300',
};
const BAR_CLS: Record<string, string> = {
  report: 'bg-sky-400',
  manifest: 'bg-slate-400',
  download: 'bg-amber-400',
  confirm: 'bg-emerald-400',
  error: 'bg-red-400',
  monitor: 'bg-slate-700',
};

export function AdminLiveUpdatesPage() {
  const [apps, setApps] = useState<Record<string, { pkg: string; name: string }>>({});
  const [manifests, setManifests] = useState<Record<Channel, OtaLiveManifest | null>>({ stable: null, platform: null });
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [devices, setDevices] = useState<Map<string, DeviceInfo>>(new Map());
  const [selected, setSelected] = useState<string | null>(null);
  const [tech, setTech] = useState(false);
  const [showMonitors, setShowMonitors] = useState(false);
  const [paused, setPaused] = useState(false);
  const [requestsConfigured, setRequestsConfigured] = useState<boolean | null>(null);
  const [requestsMessage, setRequestsMessage] = useState<string | null>(null);
  const [lastRequestPoll, setLastRequestPoll] = useState<number | null>(null);
  const [lastReportPoll, setLastReportPoll] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [, setTick] = useState(0);

  const seen = useRef<Set<string>>(new Set());
  const lastReportCreated = useRef<string | null>(null);
  const eventsRef = useRef<LiveEvent[]>([]);
  const devicesRef = useRef<Map<string, DeviceInfo>>(new Map());

  const appName = useCallback((a: string) => apps[a]?.name ?? a, [apps]);

  // ---------- ingest ----------------------------------------------------
  const noteDevice = useCallback((e: LiveEvent) => {
    const map = devicesRef.current;
    const d = map.get(e.deviceId) ?? { id: e.deviceId, vessel: null, channel: null, channelSeenAt: 0, channelHint: null };
    if (e.kind === 'report') {
      if (e.vessel) d.vessel = e.vessel;
      if (e.channelHint) d.channelHint = e.channelHint;
    } else if (e.kind === 'request' && e.method === 'GET' && e.path === '/manifest.json' && e.ts >= d.channelSeenAt) {
      d.channel = e.channel;
      d.channelSeenAt = e.ts;
    }
    map.set(e.deviceId, d);
  }, []);

  const deriveConfirms = useCallback((rep: OtaLiveReport, all: LiveEvent[], liveManifests: Record<Channel, OtaLiveManifest | null>): ConfirmEvent[] => {
    const out: ConfirmEvent[] = [];
    const dev = devicesRef.current.get(rep.deviceId);
    const channel: Channel = dev?.channel ?? dev?.channelHint ?? rep.channelHint ?? 'stable';
    for (const a of rep.apps) {
      const dl = [...all].reverse().find((e) => e.kind === 'request' && e.deviceId === rep.deviceId && e.app === a.app && e.ts < rep.ts && rep.ts - e.ts < 6 * 3_600_000 && (e.status === 200 || e.status === 206)) as OtaLiveRequest | undefined;
      if (!dl) continue;
      const offered = liveManifests[channel]?.apps?.[a.app] ?? liveManifests.stable?.apps?.[a.app];
      if (!offered || a.installedCode == null || a.installedCode < offered.versionCode) continue;
      const key = `c-${rep.id}-${a.app}`;
      if (seen.current.has(key)) continue;
      seen.current.add(key);
      out.push({ kind: 'confirm', id: key, ts: rep.ts + 1, deviceId: rep.deviceId, app: a.app, version: a.installed, code: a.installedCode, reportId: rep.id, downloadTs: dl.ts });
    }
    return out;
  }, []);

  const addEvents = useCallback((incoming: LiveEvent[], liveManifests: Record<Channel, OtaLiveManifest | null>) => {
    if (!incoming.length) return;
    const all = eventsRef.current.slice();
    let changed = false;
    for (const e of incoming.slice().sort((a, b) => a.ts - b.ts)) {
      if (seen.current.has(e.id)) continue;
      seen.current.add(e.id);
      if (e.kind === 'request' && e.monitor) { all.push(e); changed = true; continue; }
      noteDevice(e);
      all.push(e);
      changed = true;
      if (e.kind === 'report') {
        for (const c of deriveConfirms(e, all, liveManifests)) { all.push(c); }
      }
    }
    if (!changed) return;
    if (all.length > MAX_EVENTS) all.splice(0, all.length - MAX_EVENTS);
    eventsRef.current = all;
    setEvents(all);
    setDevices(new Map(devicesRef.current));
  }, [deriveConfirms, noteDevice]);

  // ---------- polling ---------------------------------------------------
  const manifestsRef = useRef(manifests);
  manifestsRef.current = manifests;

  const pollManifests = useCallback(async () => {
    try {
      const r = await api.adminOtaLiveManifests();
      setApps(r.apps);
      setManifests(r.manifests);
      manifestsRef.current = r.manifests;
    } catch { /* keep the last good copy */ }
  }, []);

  const pollRequests = useCallback(async (from: number, to: number) => {
    try {
      const r = await api.adminOtaLiveRequests({ from, to });
      setRequestsConfigured(r.configured);
      setRequestsMessage(r.message ?? null);
      if (r.configured) addEvents(r.data, manifestsRef.current);
      setLastRequestPoll(Date.now());
      setError(null);
    } catch (err) {
      setError(`Server request log: ${(err as Error).message}`);
    }
  }, [addEvents]);

  const pollReports = useCallback(async () => {
    try {
      const since = lastReportCreated.current ?? new Date(Date.now() - INITIAL_REPORT_DAYS * 86_400_000).toISOString();
      const r = await api.adminOtaLiveReports({ since, limit: 1000 });
      if (r.data.length) {
        lastReportCreated.current = new Date(Math.max(...r.data.map((x) => x.receivedTs))).toISOString();
        addEvents(r.data, manifestsRef.current);
      }
      setLastReportPoll(Date.now());
    } catch (err) {
      setError(`Device reports: ${(err as Error).message}`);
    }
  }, [addEvents]);

  useEffect(() => {
    let alive = true;
    (async () => {
      await pollManifests();
      await pollReports();
      // History: one request per hour-slice, newest first so the screen fills quickly.
      const now = Date.now();
      for (let h = 0; h < INITIAL_REQUEST_HOURS && alive; h += 1) {
        await pollRequests(now - (h + 1) * 3_600_000, now - h * 3_600_000);
        if (requestsConfigured === false) break;
      }
    })();
    const t1 = setInterval(() => { if (!paused) { const now = Date.now(); void pollRequests(now - REQUEST_LOOKBACK_MS, now); } }, REQUEST_POLL_MS);
    const t2 = setInterval(() => { if (!paused) void pollReports(); }, REPORT_POLL_MS);
    const t3 = setInterval(() => { void pollManifests(); }, MANIFEST_POLL_MS);
    const t4 = setInterval(() => setTick((n) => n + 1), 10_000);
    return () => { alive = false; clearInterval(t1); clearInterval(t2); clearInterval(t3); clearInterval(t4); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused]);

  // ---------- derived --------------------------------------------------
  const isMonitorId = (id: string) => ['test', 'deploy-script', 'rollback-script', 'check', 'fleet-monitor', 'mypolaris-admin', 'dev_selftest_monitor', 'unknown', ''].includes(id) || id.startsWith('synthetic');
  const label = (id: string) => devices.get(id)?.vessel || id;
  const channelOf = (id: string): Channel => devices.get(id)?.channel ?? devices.get(id)?.channelHint ?? 'stable';
  const offered = (id: string, app: string) => manifests[channelOf(id)]?.apps?.[app] ?? manifests.stable?.apps?.[app] ?? null;

  function deviceState(id: string) {
    const evs = events.filter((e) => e.deviceId === id);
    const reports = evs.filter((e): e is OtaLiveReport => e.kind === 'report');
    const lastReport = reports[reports.length - 1] ?? null;
    const lastServer = (evs.filter((e) => e.kind === 'request') as OtaLiveRequest[]).slice(-1)[0] ?? null;
    const downloads = (evs.filter((e) => e.kind === 'request') as OtaLiveRequest[]).filter((e) => e.app && (e.status === 200 || e.status === 206));
    const recentDl = downloads.filter((e) => Date.now() - e.ts < 15 * 60_000);
    let cls = 'na'; let text = 'No check-in seen'; let detail = '';
    if (lastReport) {
      if (lastReport.result === 'check_failed') { cls = 'bad'; text = 'Check failed'; detail = lastReport.error ?? ''; }
      else {
        const behind = lastReport.apps.filter((a) => { const o = offered(id, a.app); return o && a.installedCode != null && o.versionCode > a.installedCode; });
        const unconfirmed = recentDl.filter((r) => {
          const rep = lastReport.apps.find((a) => a.app === r.app); const o = offered(id, r.app!);
          return o && rep && (rep.installedCode ?? 0) < o.versionCode && lastReport.ts < r.ts;
        });
        if (unconfirmed.length) { cls = 'wait'; text = 'Installing, awaiting confirmation'; detail = unconfirmed.map((r) => appName(r.app!)).join(', '); }
        else if (behind.length) { cls = 'offer'; text = `${behind.length} update${behind.length > 1 ? 's' : ''} on offer`; detail = behind.map((a) => `${appName(a.app)} → ${offered(id, a.app)!.versionName}`).join(', '); }
        else { cls = 'ok'; text = 'Up to date'; }
        if (recentDl.length && Date.now() - recentDl[recentDl.length - 1].ts < 2 * 60_000 && cls !== 'wait') { cls = 'dl'; text = 'Downloading'; detail = appName(recentDl[recentDl.length - 1].app!); }
      }
    } else if (lastServer) { text = 'Server contact only'; detail = 'no check report yet'; }
    return { cls, text, detail, lastReport, lastServer };
  }

  function describe(e: LiveEvent): { cls: string; node: JSX.Element } {
    const who = <span className="font-bold mr-1.5">{label(e.deviceId)}</span>;
    if (e.kind === 'report') {
      if (e.result === 'check_failed') return { cls: 'error', node: <>{who} tried to check for updates but it failed{e.error ? `: ${e.error}` : ''}.</> };
      const behind = e.apps.filter((a) => a.availableCode != null && a.installedCode != null && a.availableCode > a.installedCode);
      const inv = e.apps.filter((a) => a.installed).map((a) => `${appName(a.app)} ${a.installed}`).join(', ');
      if (e.result === 'updates_available' || behind.length) {
        return { cls: 'report', node: <>{who} checked in: <b>{behind.length} update{behind.length === 1 ? '' : 's'} on offer</b> — {behind.map((a) => `${appName(a.app)} ${a.installed} → ${a.available}`).join(', ')}.</> };
      }
      return { cls: 'report', node: <>{who} checked in: <b>everything up to date</b>{inv ? ` (${inv})` : ''}.</> };
    }
    if (e.kind === 'confirm') return { cls: 'confirm', node: <>{who} confirmed <b>{appName(e.app)} {e.version}</b> is now installed.</> };
    const bad = e.status != null && e.status >= 400;
    if (e.monitor) return { cls: 'monitor', node: <><span className="font-bold mr-1.5">{e.deviceId || 'monitor'}</span> (monitor/probe) {e.method} {e.path} → {e.status}.</> };
    if (e.path === '/manifest.json') {
      if (bad) return { cls: 'error', node: <>{who} asked the <b>{e.channel}</b> channel what is available but the server answered HTTP {e.status}.</> };
      return { cls: 'manifest', node: <>{who} asked the <b>{e.channel}</b> channel what is available.</> };
    }
    if (e.app) {
      const o = manifests[e.channel]?.apps?.[e.app] ?? offered(e.deviceId, e.app);
      const ver = o ? ` ${o.versionName}` : '';
      const size = o ? ` (${fmtMB(o.sizeBytes)})` : '';
      if (bad) return { cls: 'error', node: <>{who} tried to download <b>{appName(e.app)}{ver}</b> from the {e.channel} channel but the server answered HTTP {e.status}.</> };
      return { cls: 'download', node: <>{who} is downloading <b>{appName(e.app)}{ver}</b>{size} from the <b>{e.channel}</b> channel{e.ms ? ` — served in ${(e.ms / 1000).toFixed(1)} s` : ''}.</> };
    }
    return { cls: bad ? 'error' : 'manifest', node: <>{who} {e.method} {e.path} → {e.status}.</> };
  }

  function techLine(e: LiveEvent): string {
    if (e.kind === 'report') {
      const rows = e.apps.map((a) => `${a.app}: ${a.installed} (${a.installedCode}) → offered ${a.available} (${a.availableCode})${a.action && a.action !== 'none' ? ` action=${a.action}` : ''}${a.error ? ` error=${String(a.error)}` : ''}`).join('\n  ');
      return `device_update_logs ${e.id.slice(2)} · device ${e.deviceId} · result=${e.result} · checked ${new Date(e.ts).toISOString()} · received ${new Date(e.receivedTs).toISOString()}\n  launcher ${e.launcher ?? '?'} · ${e.model ?? ''} · Android ${e.android ?? '?'} · wifi "${e.ssid ?? ''}" · channel-hint ${e.channelHint ?? 'unknown'}${e.lat != null && e.lon != null ? ` · ${e.lat.toFixed(4)}, ${e.lon.toFixed(4)}` : ''}\n  ${rows}`;
    }
    if (e.kind === 'confirm') return `derived: report ${e.reportId} lists ${e.app} versionCode ${e.code} ≥ download at ${new Date(e.downloadTs).toISOString()}`;
    return `${e.method} https://${e.host}${e.path} → ${e.status} (${e.outcome ?? ''})${e.ms != null ? ` in ${e.ms} ms` : ''} · device ${e.deviceId} · ${e.country ?? '?'}${e.city ? '/' + e.city : ''} ${e.ip ?? ''} · ray ${e.rayId ?? '?'}${e.channelHeader ? ` · X-Update-Channel ${e.channelHeader}` : ' · no channel header = stable'}\n  UA ${e.ua}`;
  }

  const fleetIds = useMemo(() => [...devices.keys()].filter((id) => !isMonitorId(id)), [devices]);
  const fleetRows = useMemo(() => {
    const rows = fleetIds.map((id) => ({ id, st: deviceState(id) }));
    rows.sort((a, b) => Math.max(b.st.lastReport?.ts ?? 0, b.st.lastServer?.ts ?? 0) - Math.max(a.st.lastReport?.ts ?? 0, a.st.lastServer?.ts ?? 0));
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fleetIds, events, manifests]);
  const summary = useMemo(() => {
    const n = (c: string) => fleetRows.filter((r) => r.st.cls === c).length;
    const active = events.filter((e) => !(e.kind === 'request' && e.monitor) && Date.now() - e.ts < 10 * 60_000).length;
    return { total: fleetRows.length, ok: n('ok'), offer: n('offer'), updating: n('dl') + n('wait'), bad: n('bad'), active };
  }, [fleetRows, events]);
  const feed = useMemo(() => events
    .filter((e) => (!selected || e.deviceId === selected) && (showMonitors || !(e.kind === 'request' && e.monitor)))
    .sort((a, b) => b.ts - a.ts)
    .slice(0, 400), [events, selected, showMonitors]);

  // ---------- render ---------------------------------------------------
  const live = requestsConfigured === true && lastRequestPoll != null && Date.now() - lastRequestPoll < 15_000;
  return (
    <div className={`space-y-4 ${tech ? 'tech' : ''}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <Link to="/admin/updates" className="flex items-center gap-1 text-sm text-slate-400 hover:text-white">
              <ArrowLeftIcon className="h-4 w-4" /> Update Checks
            </Link>
            <h1 className="text-xl font-semibold text-white">Live Update Monitor</h1>
            <span className="inline-flex items-center gap-2 text-sm font-semibold">
              <span className={`inline-block h-3 w-3 rounded-full ${live ? 'bg-emerald-400 animate-pulse' : requestsConfigured === false ? 'bg-red-400' : 'bg-amber-400'}`} />
              {requestsConfigured === false
                ? 'server request log not configured'
                : live ? `LIVE — polling every ${REQUEST_POLL_MS / 1000} s (Cloudflare adds a few seconds)` : 'connecting…'}
            </span>
          </div>
          <p className="text-sm text-slate-400">
            Every request a Scout makes to the update servers and every check-in it posts, per Scout, as it happens.
            {lastReportPoll ? ` Device reports last polled ${ago(lastReportPoll)}.` : ''}
          </p>
        </div>
        <div className="flex items-center gap-4 text-sm text-slate-200 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" className="h-4 w-4" checked={tech} onChange={(e) => setTech(e.target.checked)} /> Technical view</label>
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" className="h-4 w-4" checked={showMonitors} onChange={(e) => setShowMonitors(e.target.checked)} /> Show monitors &amp; probes</label>
          <button onClick={() => setPaused((p) => !p)} className="flex items-center gap-2 rounded-lg bg-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-600">
            {paused ? <PlayIcon className="h-4 w-4" /> : <PauseIcon className="h-4 w-4" />}
            {paused ? 'Resume' : 'Pause feed'}
          </button>
        </div>
      </div>

      {requestsConfigured === false && (
        <div className="rounded-lg border border-amber-700 bg-amber-900/20 p-3 text-sm text-amber-200">
          The server-side request log (manifest fetches and APK downloads) is switched off on this API: {requestsMessage}
          Device check-ins and the offered versions still update below.
        </div>
      )}
      {error && <div className="rounded-lg border border-red-800 bg-red-900/20 p-3 text-sm text-red-300">{error}</div>}

      {/* On offer */}
      <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2 text-[15px]">
        {(['stable', 'platform'] as Channel[]).map((ch) => {
          const m = manifests[ch];
          return (
            <div key={ch} className="flex flex-wrap items-center gap-2">
              <ChannelBadge channel={ch} big />
              <span className="text-slate-400 font-semibold mr-1">on offer:</span>
              {m ? APP_ORDER.filter((a) => m.apps[a]).map((a) => (
                <span key={a} className="rounded-full border border-slate-600 bg-slate-800 px-2.5 py-0.5 whitespace-nowrap text-slate-100">
                  {appName(a)} <b>{m.apps[a].versionName}</b> <span className="text-slate-400 text-[13px]">({m.apps[a].versionCode})</span>
                </span>
              )) : <span className="text-slate-400">not loaded yet</span>}
            </div>
          );
        })}
      </div>

      <div className="text-[16px] text-slate-100">
        <b>{summary.total}</b> Scouts with update activity · <b className="text-emerald-300">{summary.ok}</b> up to date · <b className="text-amber-300">{summary.offer}</b> with updates on offer · <b className="text-sky-300">{summary.updating}</b> updating now · <b className="text-red-300">{summary.bad}</b> with a problem · <b>{summary.active}</b> events in the last 10 min
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(520px,46%)_1fr]">
        {/* Fleet */}
        <div className="rounded-lg border border-slate-700 bg-slate-900/40 overflow-auto max-h-[70vh]">
          <div className="px-4 py-2 text-xs uppercase tracking-wider text-slate-400 border-b border-slate-700">Fleet — click a Scout for its own timeline</div>
          <table className="w-full text-[15px]">
            <thead className="sticky top-0 bg-slate-900">
              <tr className="text-left text-slate-400">
                <th className="px-3 py-2 font-semibold">Scout</th>
                <th className="px-3 py-2 font-semibold">Channel</th>
                <th className="px-3 py-2 font-semibold">Has (launcher)</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Last check-in</th>
                <th className="px-3 py-2 font-semibold">Last server contact</th>
              </tr>
            </thead>
            <tbody>
              {fleetRows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-slate-400">No Scout has talked to the update servers in the loaded window yet.</td></tr>
              )}
              {fleetRows.map(({ id, st }) => {
                const d = devices.get(id);
                return (
                  <tr
                    key={id}
                    onClick={() => setSelected(selected === id ? null : id)}
                    className={`cursor-pointer border-t border-slate-800 align-top hover:bg-slate-800/60 ${selected === id ? 'bg-slate-800 outline outline-2 -outline-offset-2 outline-sky-500' : ''}`}
                  >
                    <td className="px-3 py-2">
                      <div className="font-bold text-white">{label(id)}</div>
                      <div className="font-mono text-[13px] text-slate-400">{id}</div>
                    </td>
                    <td className="px-3 py-2">
                      {d?.channel ? (
                        <>
                          <ChannelBadge channel={d.channel} big title="Confirmed by the Scout's own request header" />
                          <div className="text-[13px] text-slate-400">confirmed by the Scout's own request header</div>
                        </>
                      ) : d?.channelHint ? (
                        <>
                          <ChannelBadge channel={d.channelHint} big title="From the signing key in its check report" />
                          <div className="text-[13px] text-slate-400">from its signing key; no request seen yet</div>
                        </>
                      ) : <ChannelBadge channel="unknown" big />}
                    </td>
                    <td className="px-3 py-2 text-slate-100">
                      {st.lastReport?.launcher ?? '—'}
                      {st.lastReport?.ssid && <div className="font-mono text-[13px] text-slate-400">wifi {st.lastReport.ssid}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-block rounded-md px-2.5 py-0.5 text-[14px] font-semibold ${STATUS_CLS[st.cls]}`}>{st.text}</span>
                      {st.detail && <div className="text-[13px] text-slate-400">{st.detail}</div>}
                    </td>
                    <td className="px-3 py-2 text-slate-100">
                      {st.lastReport ? <>{ago(st.lastReport.ts)}<div className="font-mono text-[13px] text-slate-400">{dmy(st.lastReport.ts)} {hms(st.lastReport.ts)}</div></> : <span className="text-slate-400">never</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-100">
                      {st.lastServer
                        ? <>{ago(st.lastServer.ts)}<div className="font-mono text-[13px] text-slate-400">{st.lastServer.path === '/manifest.json' ? 'checked manifest' : `download ${appName(st.lastServer.app ?? '')}`}</div></>
                        : <span className="text-slate-400">none in loaded window</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Feed */}
        <div className="rounded-lg border border-slate-700 bg-slate-900/40 overflow-auto max-h-[70vh]">
          <div className="flex items-center gap-3 px-4 py-2 border-b border-slate-700">
            <div className="text-xs uppercase tracking-wider text-slate-400">{selected ? `Timeline — ${label(selected)}` : 'Live feed — all Scouts'}</div>
            {selected && <button className="text-xs rounded-md bg-slate-700 px-2 py-1 text-slate-200 hover:bg-slate-600" onClick={() => setSelected(null)}>Show all Scouts</button>}
            <SignalIcon className="h-4 w-4 text-slate-500 ml-auto" />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 px-4 py-2 text-[13px] text-slate-400 border-b border-slate-800">
            <span><ChannelBadge channel="platform" /> / <ChannelBadge channel="stable" /> = which update channel the Scout is on</span>
            <span><i className="inline-block h-2.5 w-2.5 rounded-sm bg-sky-400 mr-1 align-middle" />Scout check-in report</span>
            <span><i className="inline-block h-2.5 w-2.5 rounded-sm bg-slate-400 mr-1 align-middle" />Asked what is available</span>
            <span><i className="inline-block h-2.5 w-2.5 rounded-sm bg-amber-400 mr-1 align-middle" />Downloading</span>
            <span><i className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-400 mr-1 align-middle" />Install confirmed</span>
            <span><i className="inline-block h-2.5 w-2.5 rounded-sm bg-red-400 mr-1 align-middle" />Problem</span>
          </div>
          <div className="px-4 pb-4">
            {feed.length === 0 && <div className="py-6 text-slate-400">Nothing yet. Events appear here as Scouts talk to the update servers.</div>}
            {feed.map((e) => {
              const d = describe(e);
              const badge = e.kind === 'request' && e.monitor ? null : (
                <ChannelBadge
                  channel={e.kind === 'request' ? e.channel : channelOf(e.deviceId)}
                  title={e.kind === 'request' ? (e.channelHeader ? 'X-Update-Channel header' : 'no channel header = stable') : (devices.get(e.deviceId)?.channel ? 'from request header' : 'from signing key')}
                />
              );
              return (
                <div key={e.id} className="grid grid-cols-[92px_6px_1fr] gap-3 py-2 border-b border-slate-800">
                  <div className="font-mono text-[14px] text-slate-400 whitespace-nowrap">{hms(e.ts)}<div className="text-[12px] text-slate-500">{dmy(e.ts)}</div></div>
                  <div className={`rounded-sm ${BAR_CLS[d.cls] ?? 'bg-slate-400'}`} />
                  <div>
                    <div className="text-[16px] text-slate-100">{badge}{badge ? ' ' : ''}{d.node}</div>
                    {tech && <pre className="mt-1 whitespace-pre-wrap break-all font-mono text-[13px] text-slate-400">{techLine(e)}</pre>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
